import { Message } from "@king-design/vue";
import axios, { AxiosError, type AxiosResponse } from "axios";
import {
  NETWORK_ERROR_MESSAGES_ZH,
  NETWORK_ERROR_MESSAGES_EN,
  DEFAULT_NETWORK_ERROR_MESSAGE,
  DEFAULT_REQUEST_ERROR_MESSAGE,
  hasChinese,
} from "./error-messages";
import { getKsLangType } from "../../utils/ksStorage";

export type AxiosResponseError<T, D> = AxiosError<T, D> & {
  request: any;
  response: AxiosResponse<T, D>;
  status: number;
  code: string;
};

export type AxiosNoResponseError<T, D> = AxiosError<T, D> & {
  request: any;
  response: undefined;
  status: undefined;
  code: string;
};

export type AxiosSetupError<T, D> = AxiosError<T, D> & {
  request: undefined;
  response: undefined;
  status: undefined;
};

/**
 * The request was made and the server responded with a status code
 * that falls out of the range of 2xx
 * @param err
 */
export function isResponseError<T = unknown, D = any>(
  err?: unknown,
): err is AxiosResponseError<T, D> {
  return !!err && typeof err === "object" && !!(err as any).response;
}

/**
 * The request was made but no response was received
 * `error.request` is an instance of XMLHttpRequest in the browser and an instance of
 * http.ClientRequest in node.js
 * @param err
 */
export function isNoResponseError<T = unknown, D = any>(
  err: unknown,
): err is AxiosNoResponseError<T, D> {
  const e = err as any;
  return !!err && typeof err === "object" && !e.response && !!e.request;
}

/**
 * Something happened in setting up the request that triggered an Error
 * @param err
 */
export function isSetupError<T = unknown, D = any>(err: unknown): err is AxiosSetupError<T, D> {
  const e = err as any;
  return !!err && typeof err === "object" && !e.response && !e.request;
}

const CONFIG = {
  __SKIP_CANCEL_ERROR__: true,
  __SKIP_METADATA_ERROR__: true,
};

export function eprintln(action: string, error: any) {
  const fallbackMessage = `${action || "请求"}失败`;

  console.error(fallbackMessage, error);
}

export function notifyError(action: string, error: any) {
  const fallbackMessage = `${action || "请求"}失败`;

  console.error(fallbackMessage, error);

  if (CONFIG.__SKIP_CANCEL_ERROR__ && isCancelError(error)) {
    return;
  }

  if (action && error) {
    const errMsg = getErrorMessage(error);
    if (
      CONFIG.__SKIP_METADATA_ERROR__ &&
      error instanceof Error &&
      (errMsg === "集群 ID 为空" || errMsg === "地域为空" || errMsg === "命名空间为空")
    ) {
      return;
    }

    // @ts-ignore
    Message.error(errMsg ? `${fallbackMessage}，${errMsg}` : fallbackMessage);
  }
}

export function getErrorCode(error: unknown) {
  if (!error || typeof error !== "object") return;
  const res = (error as any).response;

  if (!res) {
    return;
  }

  if (res.data && res.data.Error) {
    return res.data.Error.Code;
  }

  return;
}

export function getRequestId(error: unknown) {
  if (!error || typeof error !== "object") return "";
  if (isResponseError<any>(error)) {
    const res = error.response;
    const errData = res.data;
    if (errData) {
      return errData.RequestId;
    }
  }
  return (error as any)?.RequestId ?? "";
}

/**
 * 提取 + 归一化：后端消息语言匹配当前网站语言时返回后端消息；
 * 否则返回 HTTP 状态码映射消息（中英按网站语言）；无后端消息时走状态码/默认。
 * 供所有错误展示路径使用（RequestError.vue / notifyError / 各页面 subtitle）。
 * 兼容 string | undefined | null 入参，提升健壮性。不改写任何字段。
 */
export function getErrorMessage(error: unknown): string | undefined {
  if (typeof error === "string") return error;
  if (error == null) return undefined;

  const err = error as any;
  const isChinese = getKsLangType() === "zh";
  const table = isChinese ? NETWORK_ERROR_MESSAGES_ZH : NETWORK_ERROR_MESSAGES_EN;
  const defaultReq = isChinese
    ? DEFAULT_REQUEST_ERROR_MESSAGE.zh
    : DEFAULT_REQUEST_ERROR_MESSAGE.en;
  const defaultNet = isChinese
    ? DEFAULT_NETWORK_ERROR_MESSAGE.zh
    : DEFAULT_NETWORK_ERROR_MESSAGE.en;

  const backendMessage = getRawErrorMessage(err);
  const backendMatchesLang = isChinese
    ? hasChinese(backendMessage || "")
    : !hasChinese(backendMessage || "");
  if (backendMessage && backendMatchesLang) {
    return backendMessage;
  }

  // 后端消息不匹配语言（或无后端消息）→ 状态码映射 / 默认兜底
  if (!err.response) {
    const isTimeout = err.code === "ECONNABORTED";
    return isTimeout ? table[408] ?? defaultNet : defaultNet;
  }
  const statusCode = err.response.status;
  return table[statusCode] ?? defaultReq;
}

/**
 * 纯提取：返回后端原始业务消息，不做任何归一化。
 * 即原 getErrorMessage 逻辑。用于需要原始数据的场景（调试、透传）。
 * 兼容 string | undefined | null 入参，提升健壮性。
 */
export function getRawErrorMessage(error: unknown): string | undefined {
  if (typeof error === "string") return error;
  if (error == null) return undefined;

  const err = error as any;
  if (isResponseError<any>(err)) {
    const res = err.response;
    const errData = res.data;

    if (errData) {
      if (errData.Error) {
        return errData.Error.Message?.toString();
      }

      return (
        errData.msg ||
        errData.message ||
        errData.errmsg ||
        errData.err_msg ||
        err.message
      )?.toString();
    }
  }

  return err.message?.toString();
}

export function isCancelError(e: any) {
  return axios.isCancel(e);
}
